import { useQuery } from "@tanstack/react-query";
import { ATTRAPPE, hole } from "./klient";
import { alsAbholung, alsGespann } from "./abholung";
import type { Abholauskunft, Gespannvorschlag, Preisauskunft } from "./typen";

/* ══════════════════════════════════════════════════════════════════
   Die Preise einer ganzen Trefferliste — in einer Frage.

   **Eine Zeile beantwortet beides** (docs/38-vermietmodul.md): „frei · 2
   von 2 · 5 Tage · 100,00". Die Lage kommt quer über die Typen aus
   `GET /nachschlagen`; der Preis kam bisher je Zeile aus `GET /preis`.
   Acht Treffer waren damit acht Anfragen — bei jedem Tastendruck im
   Suchfeld.

   **Eine eigene Datei, wie beim Nachschlagen.** Der Weg hat seine eigene
   Route und seine eigene Form; was dazugehört, steht beisammen, statt in
   `typen.ts`, `routen.ts` und `haken.ts` verteilt zu liegen. Angeglichen
   wird hier und nur hier: In der Maske steht kein Feldname aus der
   Datenbank.

   **Gerechnet wird nicht hier und auch nicht drüben zweimal.** Die Route
   ruft je Typ dieselbe Preisfindung, aus der eine Position ihren Preis
   bekommt. Eine dritte Rechnung für die Liste wäre eine dritte Wahrheit.
   ══════════════════════════════════════════════════════════════════ */

type Roh = Record<string, unknown>;

const alsListe = (wert: unknown): Roh[] => (Array.isArray(wert) ? (wert as Roh[]) : []);
const txt = (wert: unknown): string => (typeof wert === "string" ? wert : "");
const zahl = (wert: unknown): number => {
  const n = typeof wert === "number" ? wert : Number(wert);
  return Number.isFinite(n) ? n : 0;
};

/** Ein Rechenschritt — i18n-Schlüssel mit Zahlen, kein fertiger Satz. */
export type Schritt = { key: string } & Record<string, unknown>;

/** Ein Zuschlag, wie die Preisfindung ihn gerechnet hat. */
export type Buendelzuschlag = {
  code: string;
  bezeichnung: string;
  netto: number;
  vorgewaehlt: boolean;
  menge: number;
  einzelpreis: number;
  einheitKey: string;
  /** Der Betrag ist erfunden (`is_placeholder`, 0032/0036) — niemand hat ihn entschieden. */
  platzhalter?: boolean;
  /** Dieser Zuschlag entfällt, wenn der Kunde selbst abholt (0127). */
  faelltBeiAbholung?: boolean;
  /** Preis immer individuell (0227, E1) — kein Satz im Stamm, „Preis fehlt". */
  preisIndividuell?: boolean;
  /** Einmal je Auftrag, nicht je Gerät (`scope = 'auftrag'`, AG-03/V-20). */
  jeAuftrag?: boolean;
};

/** Der Preis eines Typs im gefragten Zeitraum. */
export type Buendelpreis = {
  typId: string;
  /**
   * Die Ausstattung, mit der gerechnet wurde.
   *
   * Leer heißt Grundpreis. Gefüllt ist sie nur dort, wo der Typ ohne gar
   * keinen Satz führt — der Kran. Dann steht sie an der Zeile, damit
   * niemand die Zahl für den Grundpreis hält.
   */
  varianteId: string;
  netto: number;
  einzelpreis: number;
  menge: number;
  einheitKey: string;
  miettage: number;
  /** Das gerechnete Ende — bei „ab Montag, 5 Miettage" die Antwort darauf. */
  bis: string;
  /**
   * Wie viele **Kalendertage** das sind, beide Enden eingeschlossen.
   *
   * Die Route liefert sie seit jeher mit (`dienste/preisfindung.ts`,
   * `zeitraum.kalendertage`); der Klient warf sie bis zum 18.09.2026 weg.
   * Ohne sie kann die kurze Auskunft am Telefon nicht sagen, warum aus acht
   * Tagen sechs Miettage werden — und genau daran ist Birgit hängen
   * geblieben (*„Ich bin bei 8 Tagen, und da stehen 6."*).
   */
  kalendertage: number;
  /** Warum aus den Kalendertagen so viele Miettage werden: Schlüssel `preis.*`. */
  miettageSchritte: Schritt[];
  bedient: boolean;
  bedienungspflicht: "ohne_person" | "nur_mit_person" | "wahlweise";
  schritte: Schritt[];
  zuschlaege: Buendelzuschlag[];
  /**
   * **Auf diesen Preis schlägt nichts auf** (Migration 0144).
   *
   * `zuschlaege` ist dann leer — und eine leere Liste sieht aus wie „für
   * dieses Gerät gibt es keine Regeln", nicht wie „hier gilt bewusst keine".
   * Die Auskunft schreibt es darum hin (`Zuschlagsanzeige`).
   */
  zuschlagsfrei: boolean;
  /**
   * Es gibt keinen Preis — und warum.
   *
   * `kein_tarif` heißt: Im Stamm steht für diesen Typ kein Satz.
   * `nicht_gefunden` heißt: Den Typ gibt es nicht (mehr). Beides steht an
   * der Zeile und lässt die anderen sieben Antworten stehen.
   */
  ohnePreis: "" | "kein_tarif" | "nicht_gefunden";
  /** Ob der Kunde selbst abholen kann, und welcher Anhänger dann mitgeht (0127). */
  abholung?: Abholauskunft;
  /** Der Auflieger, der hinter diese Zugmaschine gehört (0072) — oder nichts. */
  gespann?: Gespannvorschlag | null;
};

export type Preisbuendel = {
  von: string;
  bis: string;
  /** Die Miettage nach dem Kalender des Betriebs — für die Zeile neben dem Feld. */
  kalender: { von: string; bis: string; miettage: number; schritte: Schritt[] };
  zeilen: Buendelpreis[];
};

export type Buendelfrage = {
  typen: string[];
  von: string;
  /** Das Enddatum. Fehlt es, sagt `miettage`, wie lang gemietet wird. */
  bis?: string | null;
  /** So viele **Miettage** ab `von` — das Ende rechnet die API. */
  miettage?: number | null;
  /** Die Kundennummer, damit die Kondition mitrechnet. */
  kunde?: string | null;
  bedient?: boolean;
};

const alsSchritte = (wert: unknown): Schritt[] =>
  alsListe(wert)
    .filter((x) => typeof x["key"] === "string")
    .map((x) => ({ ...x, key: x["key"] as string }));

function alsPreis(z: Roh): Buendelpreis {
  const schritte = alsSchritte(z["schritte"]);
  const zeitraum = (z["zeitraum"] ?? {}) as Roh;
  const pflicht = txt(z["bedienungspflicht"]);
  return {
    typId: txt(z["typ_id"]),
    varianteId: txt(z["variante_id"]),
    netto: zahl(z["netto"]),
    einzelpreis: zahl(z["einzelpreis"]),
    menge: zahl(z["menge"]),
    einheitKey: txt(z["einheit_key"]),
    miettage: zahl(z["miettage"]),
    bis: txt(zeitraum["bis"]),
    kalendertage: zahl(zeitraum["kalendertage"]),
    miettageSchritte: alsSchritte(z["miettage_schritte"]),
    bedient: z["bedient"] === true,
    bedienungspflicht:
      pflicht === "nur_mit_person" || pflicht === "ohne_person" ? pflicht : "wahlweise",
    schritte,
    zuschlaege: alsListe(z["zuschlaege"]).map((x) => ({
      code: txt(x["code"]),
      bezeichnung: txt(x["label"] ?? x["bezeichnung"]) || txt(x["code"]),
      netto: zahl(x["netto"]),
      vorgewaehlt: x["vorgewaehlt"] === true || x["preselected"] === true,
      menge: zahl(x["menge"]) || 1,
      einzelpreis: zahl(x["einzelpreis"]),
      einheitKey: txt(x["einheit_key"]),
      ...(x["platzhalter"] === true ? { platzhalter: true } : {}),
      ...(x["faellt_bei_abholung"] === true ? { faelltBeiAbholung: true } : {}),
      ...(x["preis_individuell"] === true ? { preisIndividuell: true } : {}),
      ...(x["umfang"] === "auftrag" ? { jeAuftrag: true } : {}),
    })),
    // Fehlt es — in jeder älteren Antwort —, gilt „nein" (0144).
    zuschlagsfrei: z["zuschlagsfrei"] === true,
    abholung: alsAbholung(z["abholung"]),
    gespann: alsGespann(z["gespann"]),
    ohnePreis: txt(z["fehler"]) === "nicht_gefunden"
      ? "nicht_gefunden"
      : schritte.some((s) => s.key === "preis.kein_tarif" || s.key === "preis.variante_noetig")
        ? "kein_tarif"
        : "",
  };
}

export async function preisbuendel(frage: Buendelfrage): Promise<Preisbuendel> {
  const leer: Preisbuendel = {
    von: frage.von,
    bis: frage.bis ?? frage.von,
    kalender: { von: frage.von, bis: frage.bis ?? frage.von, miettage: 0, schritte: [] },
    zeilen: [],
  };
  if (ATTRAPPE) return leer;

  const teile = new URLSearchParams({ von: frage.von });
  if (frage.typen.length) teile.set("typ", frage.typen.join(","));
  // Eins von beiden, nie beides: Zwei Angaben wären zwei Fragen.
  if (frage.bis) teile.set("bis", frage.bis);
  else teile.set("miettage", String(frage.miettage ?? 1));
  if (frage.kunde) teile.set("kunde", frage.kunde);
  if (frage.bedient) teile.set("bedient", "1");

  const roh = await hole<Roh>(`/preise?${teile.toString()}`);
  const kalender = (roh["kalender"] ?? {}) as Roh;
  return {
    von: txt(roh["von"]),
    bis: txt(roh["bis"]),
    kalender: {
      von: txt(kalender["von"]),
      bis: txt(kalender["bis"]),
      miettage: zahl(kalender["miettage"]),
      schritte: alsSchritte(kalender["schritte"]),
    },
    zeilen: alsListe(roh["zeilen"]).map(alsPreis),
  };
}

/**
 * Die Preise zu einer Trefferliste.
 *
 * **Die letzte Antwort bleibt stehen, solange die neue unterwegs ist**
 * (`placeholderData`) — dieselbe Überlegung wie bei `usePreis` und beim
 * Nachschlagen: Am Telefon springt der Schirm sonst bei jedem Buchstaben.
 *
 * Ohne Typen wird nicht gefragt. Die Antwort auf eine leere Liste ist eine
 * leere Liste, und dafür braucht es keinen Aufruf — aber der Kalender oben
 * wird trotzdem gebraucht, sobald ein Zeitraum dasteht. Deshalb genügt ein
 * Zeitraum: Die Route antwortet dann mit `kalender` und ohne Zeilen.
 */
export function usePreisbuendel(frage: Buendelfrage, an = true) {
  const typen = frage.typen.join(",");
  const nachDauer = !frage.bis && Boolean(frage.miettage);
  return useQuery({
    queryKey: [
      "preisbuendel", typen, frage.von, frage.bis ?? "",
      nachDauer ? frage.miettage : 0, frage.kunde ?? "", frage.bedient === true,
    ] as const,
    queryFn: () => preisbuendel(frage),
    enabled: an && Boolean(frage.von && (frage.bis || nachDauer)),
    placeholderData: (vorher: Preisbuendel | undefined) => vorher,
  });
}


/* ══════════════════════════════════════════════════════════════════
   Eine Einzelauskunft in der Form der Liste.

   **Warum es das braucht** (18.09.2026). Am Telefon steht der Preis an
   der Zeile — gerechnet über `GET /preise` für alle Treffer auf einmal.
   Wer die Zeile aufklappt und darin die Ausstattung wechselt oder den
   Bediener dazunimmt, stellt eine andere Frage; sie geht als `GET /preis`
   für diesen einen Typ hinaus.

   **Dieselbe Anzeige muss beide Antworten tragen.** Sonst stünden an
   derselben Stelle zwei Formen desselben Preises, und die kurze Auskunft
   müsste je nachdem anders lesen — der Anfang von zwei Zahlen, die beide
   richtig sind (28-designkonzept.md). Also wird die Einzelauskunft
   hierher gegossen, und die Maske kennt nur noch `Buendelpreis`.

   **Gerechnet wird dabei nichts.** Beide Routen rufen dieselbe
   Preisfindung; hier werden nur Feldnamen angeglichen — an der einen
   Stelle, an der das in diesem Weg passiert.
   ══════════════════════════════════════════════════════════════════ */
export function alsBuendelpreis(
  typId: string,
  p: Preisauskunft,
  /** Das Ende, nach dem gefragt wurde — falls die Antwort keines nennt. */
  bisGefragt: string,
  /** Die Ausstattung, mit der gefragt wurde. Die Antwort trägt sie nicht. */
  varianteId: string,
): Buendelpreis {
  const schritte = p.schritte ?? [];
  return {
    typId,
    varianteId,
    netto: p.netto,
    einzelpreis: p.einzelpreis,
    menge: p.menge,
    einheitKey: p.einheit_key,
    miettage: p.miettage,
    bis: p.zeitraum?.bis ?? bisGefragt,
    kalendertage: p.zeitraum?.kalendertage ?? 0,
    miettageSchritte: p.miettageSchritte ?? [],
    bedient: p.bedient === true,
    bedienungspflicht: p.bedienungspflicht === "nur_mit_person" || p.bedienungspflicht === "ohne_person"
      ? p.bedienungspflicht
      : "wahlweise",
    schritte,
    zuschlaege: (p.zuschlaege ?? []).map((x) => ({
      code: x.code,
      bezeichnung: x.bezeichnung,
      netto: x.netto,
      vorgewaehlt: x.vorgewaehlt,
      menge: x.menge || 1,
      einzelpreis: x.einzelpreis,
      einheitKey: x.einheit_key,
      ...(x.platzhalter ? { platzhalter: true } : {}),
      ...(x.faelltBeiAbholung ? { faelltBeiAbholung: true } : {}),
      ...(x.preisIndividuell ? { preisIndividuell: true } : {}),
      ...(x.jeAuftrag ? { jeAuftrag: true } : {}),
    })),
    zuschlagsfrei: p.zuschlagsfrei === true,
    ...(p.abholung ? { abholung: p.abholung } : {}),
    ...(p.gespann !== undefined ? { gespann: p.gespann } : {}),
    /* Ein Typ ohne Satz kommt auch hier mit seinem Rechenweg zurück — und
       die Zeile schreibt daneben, was fehlt, statt eine Null zu nennen. */
    ohnePreis: schritte.some((s) => s.key === "preis.kein_tarif" || s.key === "preis.variante_noetig")
      ? "kein_tarif"
      : "",
  };
}
